import { Typography, Card, Tag, Divider, Space, Badge } from "antd";
import {
    DeleteOutlined,
    InfoCircleOutlined,
    AppstoreOutlined,
    MailOutlined,
    SafetyOutlined,
    TeamOutlined,
    ClockCircleOutlined,
    CheckCircleFilled,
} from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;

const features = [
    {
        icon: <SafetyOutlined style={{ color: "#1677ff" }} />,
        text: "Secure login using Employee ID and password",
    },
    {
        icon: <SafetyOutlined style={{ color: "#1677ff" }} />,
        text: "OTP-based authentication",
    },
    {
        icon: <ClockCircleOutlined style={{ color: "#1677ff" }} />,
        text: "Attendance tracking",
    },
    {
        icon: <TeamOutlined style={{ color: "#1677ff" }} />,
        text: "Employee data management",
    },
];

const SectionHeader = ({ icon, label, color = "#1677ff" }) => (
    <Space align="center" style={{ marginBottom: 12 }}>
        <span
            style={{
                background: `${color}15`,
                border: `1px solid ${color}30`,
                borderRadius: 8,
                padding: "6px 8px",
                display: "flex",
                alignItems: "center",
                fontSize: 16,
                color,
            }}
        >
            {icon}
        </span>
        <Text strong style={{ fontSize: 15, color: "#1a1a1a" }}>
            {label}
        </Text>
    </Space>
);

export default function AppPolicyInfo() {
    return (
        <div
            style={{
                minHeight: "100vh",
                background: "#f5f7fa",
                padding: "40px 16px",
                fontFamily: "'Segoe UI', sans-serif",
            }}
        >
            <div style={{ maxWidth: 620, margin: "0 auto" }}>
                {/* Header */}
                <div style={{ textAlign: "center", marginBottom: 32 }}>
                    <Badge
                        count={
                            <CheckCircleFilled style={{ color: "#52c41a", fontSize: 18 }} />
                        }
                        offset={[-4, 4]}
                    >
                        <div
                            style={{
                                width: 64,
                                height: 64,
                                background: "linear-gradient(135deg, #1677ff, #0958d9)",
                                borderRadius: 16,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 8px 24px #1677ff40",
                                marginBottom: 4,
                            }}
                        >
                            <AppstoreOutlined style={{ fontSize: 30, color: "#fff" }} />
                        </div>
                    </Badge>
                    <Title level={3} style={{ marginTop: 16, marginBottom: 4, color: "#1a1a1a" }}>
                        Baashyaam Employee App
                    </Title>
                    <Tag color="blue" style={{ borderRadius: 20, padding: "2px 12px" }}>
                        Internal Use Only
                    </Tag>
                </div>

                {/* Short Description */}
                <Card
                    style={{
                        borderRadius: 12,
                        marginBottom: 16,
                        border: "1px solid #e6eaf0",
                        boxShadow: "0 2px 8px #0000000a",
                    }}
                    bodyStyle={{ padding: "20px 24px" }}
                >
                    <SectionHeader
                        icon={<InfoCircleOutlined />}
                        label="About This App"
                    />
                    <Paragraph style={{ color: "#555", margin: 0, lineHeight: 1.7 }}>
                        Employee attendance and management application for internal company
                        use.
                    </Paragraph>
                </Card>

                {/* Full Description */}
                <Card
                    style={{
                        borderRadius: 12,
                        marginBottom: 16,
                        border: "1px solid #e6eaf0",
                        boxShadow: "0 2px 8px #0000000a",
                    }}
                    bodyStyle={{ padding: "20px 24px" }}
                >
                    <SectionHeader
                        icon={<AppstoreOutlined />}
                        label="Full Description"
                    />
                    <Paragraph style={{ color: "#555", marginBottom: 16, lineHeight: 1.7 }}>
                        This application is designed for employees to manage attendance and
                        internal operations within the organization.
                    </Paragraph>

                    <Text
                        strong
                        style={{ display: "block", marginBottom: 12, color: "#1a1a1a" }}
                    >
                        Features
                    </Text>
                    <Space direction="vertical" size={10} style={{ width: "100%" }}>
                        {features.map((f, i) => (
                            <div
                                key={i}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    background: "#f8faff",
                                    border: "1px solid #e8eef8",
                                    borderRadius: 8,
                                    padding: "10px 14px",
                                }}
                            >
                                {f.icon}
                                <Text style={{ color: "#444", fontSize: 14 }}>{f.text}</Text>
                            </div>
                        ))}
                    </Space>

                    <Divider style={{ margin: "16px 0" }} />
                    <div
                        style={{
                            background: "#fffbe6",
                            border: "1px solid #ffe58f",
                            borderRadius: 8,
                            padding: "10px 14px",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                        }}
                    >
                        <SafetyOutlined style={{ color: "#d48806" }} />
                        <Text style={{ color: "#7c5800", fontSize: 13 }}>
                            This application is intended for <strong>authorized employees only</strong>.
                        </Text>
                    </div>
                </Card>

                {/* Account Deletion Policy */}
                <Card
                    style={{
                        borderRadius: 12,
                        border: "1px solid #ffe0e0",
                        boxShadow: "0 2px 8px #0000000a",
                        background: "#fffafa",
                    }}
                    bodyStyle={{ padding: "20px 24px" }}
                >
                    <SectionHeader
                        icon={<DeleteOutlined />}
                        label="Account Deletion Policy"
                        color="#ff4d4f"
                    />
                    <Paragraph style={{ color: "#555", marginBottom: 14, lineHeight: 1.7 }}>
                        Users can request deletion of their account and associated data by
                        contacting us via email. All requests will be processed within a
                        reasonable timeframe.
                    </Paragraph>

                    <div
                        style={{
                            background: "#fff1f0",
                            border: "1px solid #ffccc7",
                            borderRadius: 8,
                            padding: "12px 16px",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                        }}
                    >
                        <MailOutlined style={{ color: "#ff4d4f", fontSize: 16 }} />
                        <div>
                            <Text style={{ color: "#888", fontSize: 12, display: "block" }}>
                                Contact Email
                            </Text>
                            <Text
                                strong
                                copyable
                                style={{ color: "#ff4d4f", fontSize: 14 }}
                            >
                                hr@baashyaam.com
                            </Text>
                        </div>
                    </div>
                </Card>

                <Text
                    style={{
                        display: "block",
                        textAlign: "center",
                        color: "#bbb",
                        fontSize: 12,
                        marginTop: 24,
                    }}
                >
                    © Baashyaam Group · All rights reserved
                </Text>
            </div>
        </div>
    );
}